
import { motion } from 'framer-motion';

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
  onSend: () => void;
}

export default function FileUploadPreview({ file, onRemove, onSend }: FilePreviewProps) {
  // Determine file type icon based on file extension
  const getFileIcon = () => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (extension === 'pdf') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M20 8.94C19.9896 8.84813 19.9695 8.75763 19.94 8.67V8.58C19.8919 8.47718 19.8278 8.38267 19.75 8.3L13.75 2.3C13.6673 2.22222 13.5728 2.15808 13.47 2.11C13.4402 2.10576 13.4099 2.10576 13.38 2.11C13.2784 2.05174 13.1662 2.01434 13.05 2H7C6.46957 2 5.96086 2.21071 5.58579 2.58579C5.21071 2.96086 5 3.46957 5 4V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V9C19 9 19 9 19 8.94ZM14 5.41L15.59 7H14V5.41ZM17 20H7V4H12V7C12 7.26522 12.1054 7.51957 12.2929 7.70711C12.4804 7.89464 12.7348 8 13 8H17V20Z" 
            fill="#E74C3C"
          />
          <path 
            d="M14.25 13C14.25 13.7 13.9 14.25 13.4 14.55C13.9 14.85 14.35 15.4 14.35 16.15C14.35 17.25 13.45 18 12.35 18H9.5V10H12.15C13.2 10 14.25 10.65 14.25 13ZM11 11.5V13H12.15C12.6 13 12.75 12.5 12.75 12C12.75 11.5 12.45 11.5 12.15 11.5H11ZM11 14.5V16.5H12.35C12.8 16.5 12.85 16 12.85 15.5C12.85 14.85 12.45 14.5 12.1 14.5H11Z" 
            fill="#E74C3C"
          />
        </svg>
      );
    } else if (['doc', 'docx', 'txt', 'rtf'].includes(extension || '')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" 
            fill="#4B81FF"
          />
          <path 
            d="M8 14H16V16H8V14ZM8 10H16V12H8V10Z" 
            fill="#4B81FF"
          />
        </svg>
      );
    } else if (['xls', 'xlsx', 'csv'].includes(extension || '')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" 
            fill="#27AE60"
          />
          <path 
            d="M7 13H9V17H7V13ZM11 7H13V17H11V7ZM15 10H17V17H15V10Z" 
            fill="#27AE60"
          />
        </svg>
      );
    } else {
      // Generic file icon
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM13 9V3.5L18.5 9H13Z" fill="#4b81ff"/>
        </svg>
      );
    }
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm border border-[#edf0f5] p-3 mb-4 flex items-center"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex-shrink-0 bg-[#f6f8ff] p-2 rounded-md mr-3">
        {getFileIcon()}
      </div>
      
      <div className="flex-grow min-w-0">
        <p className="text-[15px] font-medium text-[#333333] truncate">{file.name}</p>
        <p className="text-[13px] text-[#717182]">{formatFileSize(file.size)}</p>
      </div>
      
      <div className="flex items-center gap-2 ml-3">
        <button 
          onClick={onRemove}
          className="text-[#717182] hover:text-[#464C5C] transition-colors p-1.5 rounded-full hover:bg-[#f6f8ff]"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
          </svg>
        </button>
        
        <button 
          onClick={onSend}
          className="bg-[#165bfb] text-white rounded-full p-1.5 hover:bg-[#2044cc] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </motion.div>
  );
}
